import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import useIsT2Experience from 'src/hooks/useIsT2Experience';
import { setIsTldAlertModalOpen, setJmrResults } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import {
  canJobBeModified,
  createDataCyForJob,
  isJobAtCapacity,
  JmrJobTypes,
  type TableDataRow,
} from 'src/utils/jmrUtils';
import {
  areAllModifiedT2JobsValid,
  areConflictsResolved,
  getJobError,
  toggleJob,
} from 'src/utils/placementSelectionUtils';
import { filterTableRows } from 'src/utils/tableUtils';

import TldAlertModal from '../../tldAlertModal/tldAlertModal';

import ApplyModificationsModal from './applyModificationsModal';
import ConflictPopover from './conflictPopover';
import T2PlusModificationsModal from './t2PlusModificationsModal';

interface JobMatchesTableProps {
  searchField: string;
  jobsExceedingRestrictionsInAggregate: Set<string>;
  exceededRestrictionIds: Set<number>;
  /** For scrolling job row into view */
  selectedJobRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  stickyHeight: number;
}

const JobMatchesTable = ({
  searchField,
  jobsExceedingRestrictionsInAggregate,
  exceededRestrictionIds,
  selectedJobRefs,
  stickyHeight,
}: JobMatchesTableProps) => {
  const { jmrFilter, jmrResults, backgroundFormData, tldAlertModal } = useAppSelector((state) => state.newJmrPage);
  const dispatch = useAppDispatch();
  const [tableData, setTableData] = useState(jmrResults);
  const [jobToModify, setJobToModify] = useState<null | TableDataRow>(null);
  const idToFocus = useRef<string | null>(null);
  const isT2Experience = useIsT2Experience();

  const focusSelection = (resultId: string) => {
    idToFocus.current = resultId;
  };

  /**
   * useLayoutEffect fires synchronously after the DOM has been updated, guaranteeing that
   * selectedJobRefs is populated before we attempt to focus. No dependency array is intentional —
   * the null guard makes this a no-op on most renders, and omitting the array avoids using
   * an unrelated value as a misleading timing mechanism.
   */
  useLayoutEffect(() => {
    if (idToFocus.current === null) return;
    if (!selectedJobRefs.current.has(idToFocus.current)) return;
    selectedJobRefs.current.get(idToFocus.current)?.focus();
    idToFocus.current = null;
  });

  useEffect(() => {
    // filter, sorting, and pagination
    const filteredData = filterTableRows(jmrResults, searchField, [
      'shiftId',
      'jobId',
      'totalHours',
      'perfectMatch',
      'isChecked',
      'rankingScore',
    ]).filter((row) => row.jobType === jmrFilter.jobType);
    setTableData(filteredData);
  }, [jmrResults, jmrFilter.jobType, searchField]);

  const setTotalHours = (id: string, totalHours: string) => {
    dispatch(
      setJmrResults(jmrResults.map((jmrResult) => (jmrResult.id === id ? { ...jmrResult, totalHours } : jmrResult))),
    );
  };

  const onChecked = (resultId: string) => {
    if (jmrFilter.jobType === JmrJobTypes.TLD && !tldAlertModal.hasBeenOpened) {
      dispatch(
        setIsTldAlertModalOpen({
          open: true,
          resultId,
        }),
      );
    } else {
      toggleJob(resultId, jmrResults, dispatch);
      focusSelection(resultId);
    }
  };

  const onClickModify = (row: TableDataRow) => {
    if (jmrFilter.jobType === JmrJobTypes.TLD && !tldAlertModal.hasBeenOpened) {
      dispatch(
        setIsTldAlertModalOpen({
          open: true,
          resultId: row.id,
        }),
      );
    } else {
      setJobToModify(row);
    }
  };

  const onConfirmTldModal = () => {
    const job = tableData.find((row) => row.id === tldAlertModal.resultId);
    if (!job) {
      return;
    }

    if (job.conflicts?.length && !job.maximumHoursAssociateCanDo) {
      setJobToModify(job);
    } else {
      toggleJob(tldAlertModal.resultId, jmrResults, dispatch);
      focusSelection(tldAlertModal.resultId);
    }
  };

  return (
    <>
      <Table
        layout="auto"
        fixHeaderRows
        fixHeaderRowsOffset={stickyHeight}
        headerRows={1}
        showStripes={true}
        spacing="small"
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        <TableRow>
          <TableCell sortColumn="rank">Job</TableCell>
          <TableCell sortColumn="name">Shift</TableCell>
          <TableCell>Conflicts</TableCell>
          <TableCell>Action</TableCell>
          <TableCell></TableCell>
          <TableCell width={80}></TableCell>
        </TableRow>
        {tableData.map((row, index) => {
          return (
            <TableRow key={index} highlightOnHover={true}>
              <TableCell>
                <Column spacing="200">
                  <Text type="b300">{row.name}</Text>

                  <Text type="b200" color="secondary">
                    {row.jobType}
                  </Text>
                </Column>
              </TableCell>
              <TableCell>
                {!!row.shiftCode && (
                  <Column spacing="200">
                    <Text type="b300">{row.shiftCode}</Text>
                    <Text type="b200" color="secondary">
                      {`${row?.jobCapacity || 0} hrs capacity`}
                    </Text>
                  </Column>
                )}
              </TableCell>
              <TableCell>
                <Column spacing="200">
                  <ConflictPopover jobMatch={row} {...{ exceededRestrictionIds }} />
                  <Text type="b200" color="secondary">
                    {areConflictsResolved(row) ? 'Full Shift' : `Maximum hrs: ${row.maximumHoursAssociateCanDo}`}
                  </Text>
                </Column>
              </TableCell>

              <TableCell>
                {isT2Experience ? (
                  <T2ModifyBtn row={row} onClickModify={onClickModify} />
                ) : (
                  <T1ModifyBtn row={row} onClickModify={onClickModify} />
                )}
              </TableCell>

              <TableCell>
                {row.maximumHoursAssociateCanDo === 0 || isJobAtCapacity(row) ? (
                  <Tooltip
                    title={
                      isJobAtCapacity(row)
                        ? 'Capacity is full.'
                        : 'Apply job modifications to enable selection of this job.'
                    }
                    position="top"
                  >
                    <Checkbox disabled={true} />
                  </Tooltip>
                ) : (
                  <Checkbox
                    disabled={
                      (!row.isChecked && isT2Experience && jmrResults.some((r) => r.isChecked)) ||
                      (isT2Experience && row.tierTwoPlusJobModified && !row.modificationNotes?.trim().length)
                    }
                    checked={row.isChecked}
                    onChange={() => onChecked(row.id)}
                    data-cy={`${createDataCyForJob(row.name)}-checkbox`}
                    aria-label={`${row.name} Checkbox`}
                  />
                )}
              </TableCell>

              <TableCell width={80} alignmentVertical="top">
                <div className="jobMatchesTable__input-wrapper">
                  {row.isChecked ? (
                    <Input
                      // Callback refs are called with the DOM element on mount and `null` on unmount.
                      // We use this to keep selectedJobRefs in sync with the DOM:
                      // - On mount (ref is truthy): register the input so focusSelection() can find it
                      // - On unmount (ref is null): remove the stale entry, otherwise re-selecting the same
                      //   row would call .focus() on a detached DOM node and silently fail
                      ref={(ref) =>
                        ref ? selectedJobRefs.current.set(row.id, ref) : selectedJobRefs.current.delete(row.id)
                      }
                      type="text"
                      value={row.totalHours.toString()}
                      onChange={(totalHours: string) => setTotalHours(row.id, totalHours)}
                      error={!!getJobError(row, jmrResults, backgroundFormData, jobsExceedingRestrictionsInAggregate)}
                      errorMessage={getJobError(
                        row,
                        jmrResults,
                        backgroundFormData,
                        jobsExceedingRestrictionsInAggregate,
                      )}
                      size="small"
                      placeholder="hrs..."
                      data-cy={`${createDataCyForJob(row.name)}-totalHours`}
                    />
                  ) : (
                    ''
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </Table>

      <TldAlertModal
        isOpen={tldAlertModal.open}
        setIsOpen={(open) =>
          dispatch(
            setIsTldAlertModalOpen({
              open,
            }),
          )
        }
        onConfirm={() => onConfirmTldModal()}
      />

      {isT2Experience ? (
        <T2PlusModificationsModal
          jobToModify={jobToModify}
          isOpen={!!jobToModify}
          onClose={() => setJobToModify(null)}
        />
      ) : (
        <ApplyModificationsModal
          jobToModify={jobToModify}
          exceededRestrictionIds={exceededRestrictionIds}
          isOpen={!!jobToModify}
          focusSelection={focusSelection}
          onClose={() => setJobToModify(null)}
        />
      )}
    </>
  );
};

const T1ModifyBtn = ({ row, onClickModify }: { row: TableDataRow; onClickModify: (row: TableDataRow) => void }) => {
  if (!canJobBeModified(row)) return <></>;
  return (
    <Button
      type="tertiary"
      data-cy={`${createDataCyForJob(row.name)}-modify`}
      onClick={() => onClickModify(row)}
      aria-label={`Modify ${row.name}`}
    >
      Modify
    </Button>
  );
};

const T2ModifyBtn = ({ row, onClickModify }: { row: TableDataRow; onClickModify: (row: TableDataRow) => void }) => {
  if (!row.tierTwoPlusJobModified) return <></>;
  const label = row.modificationNotes?.trim().length ? 'Modify' : 'Info Required';
  return (
    <Button type="link" onClick={() => onClickModify(row)} aria-label={`${label} ${row.name}`}>
      {label}
    </Button>
  );
};

export default JobMatchesTable;
